import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Badge, Dropdown, Layout, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
  AppstoreFilled,
  AppstoreOutlined,
  BookOutlined,
  BulbFilled,
  BulbOutlined,
  CarryOutOutlined,
  DownOutlined,
  HistoryOutlined,
  LoadingOutlined,
  LogoutOutlined,
  MessageOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ProjectOutlined,
  RightOutlined,
  SearchOutlined,
  SettingOutlined,
  ApiOutlined,
  StarFilled,
  StarOutlined,
  TableOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { Space, View } from '@lens/shared';
import { useAuth } from '../../features/auth/AuthContext.tsx';
import { NotificationBell } from '../../features/notifications/NotificationBell.tsx';
import { useNotifications } from '../../features/notifications/NotificationsContext.tsx';
import { useAiPanel } from '../../features/ai/AiPanelContext.tsx';
import { AiChatPanel } from '../../features/ai/AiChatPanel.tsx';
import { HermesSessionDock } from '../../features/hermes/HermesSessionDock.tsx';
import { useHermesSessions } from '../../features/hermes/HermesSessionsContext.tsx';
import { JobsIndicator } from '../../features/jobs/JobsIndicator.tsx';
import { ReleasesButton } from '../../features/releases/ReleasesButton.tsx';
import { AiSparkIcon } from '../components/AiSparkIcon.tsx';
import { listSpaces } from '../../features/spaces/api.ts';
import { listViews } from '../../features/views/api.ts';
import { onSpacesChanged } from '../lib/spacesBus.ts';
import { CommandPalette } from './CommandPalette.tsx';
import { FavoritesSection } from './FavoritesSection.tsx';
import {
  type FavoriteEntry,
  favoriteKey,
  getFavorites,
  subscribeFavorites,
  toggleFavorite,
} from '../prefs/store.ts';
import { SpaceAvatar } from '../components/SpaceAvatar.tsx';
import { getSpaceIconDef, resolveSpaceIconKey, resolveSpaceColor } from '../components/spaceIcons.ts';
import { useThemeMode } from '../theme/ThemeProvider.tsx';
import styles from './AppLayout.module.css';

const { Sider, Content } = Layout;

/**
 * Глобальный лэйаут с левым рельсом-навигацией (Sider).
 *
 * Рельс:
 *   бренд + кнопка сворачивания → ⌘K-поиск → навигация (Пространства /
 *   Справочники / Уведомления с бейджем) → дерево «пространства →
 *   представления» (каждое пространство и его список представлений
 *   сворачиваются) → подвал (Lensy при canUseAi, user-dropdown).
 *
 * Рельс целиком сворачивается в узкий icon-режим. Состояние свёрнутости и
 * раскрытых пространств персистится в localStorage.
 */

const LS_COLLAPSED = 'lens:rail:collapsed';
const LS_EXPANDED = 'lens:rail:expandedSpaces';

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function readLsBool(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function readLsSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function writeLs(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function viewIcon(v: View): React.ReactNode {
  // Выбранная иконка из курируемого набора ('i:<key>') — рендерим её;
  // иначе нейтральная иконка по типу представления (таблица / канбан).
  // Эмодзи как системные глифы не показываем (серьёзный тон).
  const def = getSpaceIconDef(resolveSpaceIconKey(v.icon) ?? '');
  if (def) return <def.Icon size={15} strokeWidth={1.9} color={resolveSpaceColor(v.icon) ?? 'currentColor'} style={{ verticalAlign: '-2px' }} />;
  return v.viewType === 'kanban' ? <ProjectOutlined /> : <TableOutlined />;
}

export function AppLayout({ children }: PropsWithChildren): JSX.Element {
  const { user, logout, canUseAi } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { togglePanel } = useAiPanel();
  const {
    sessions: hermesSessions,
    open: hermesOpen,
    drawerWidth: hermesDrawerWidth,
    showDrawer: showHermesDrawer,
    closeDrawer: closeHermesDrawer,
  } = useHermesSessions();
  // Панель оценки — overlay справа; чтобы грид (и его правая колонка действий)
  // не прятались под ней, сдвигаем контент влево на ширину панели.
  const hermesShift = hermesOpen && hermesSessions.length > 0 ? hermesDrawerWidth : 0;
  const { mode, toggle: toggleTheme } = useThemeMode();

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mySpaces, setMySpaces] = useState<Space[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(() => readLsBool(LS_COLLAPSED));
  const [expanded, setExpanded] = useState<Set<string>>(() => readLsSet(LS_EXPANDED));
  const [viewsBySpace, setViewsBySpace] = useState<Record<string, View[]>>({});
  const [loadingViews, setLoadingViews] = useState<Set<string>>(new Set());
  const { unreadBySpace, reload: reloadUnread } = useNotifications();
  const [favorites, setFavorites] = useState<FavoriteEntry[]>(() => getFavorites());

  // Избранное живёт в персональных преференсах; звёздочку жмут из разных мест
  // рельса — подписываемся, чтобы секция и звёзды всегда были согласованы.
  // hydratePreferences (после логина) тоже дёргает подписчиков через writePref
  // не проходит, поэтому синхронизируемся ещё и при смене пользователя.
  useEffect(() => subscribeFavorites(() => setFavorites(getFavorites())), []);
  useEffect(() => {
    setFavorites(getFavorites());
  }, [user]);

  // Быстрый lookup «в избранном ли цель» для звёзд на строках дерева.
  const favKeys = useMemo(
    () => new Set(favorites.map((f) => favoriteKey(f.kind, f.id))),
    [favorites],
  );

  // id → имя пространства: бейдж у одноимённых представлений в «Избранном».
  const spaceNameById = useMemo(
    () => new Map(mySpaces.map((s) => [s.id, s.name])),
    [mySpaces],
  );

  // Активное пространство/представление из URL. Поддерживаем обе формы:
  // короткую `/s/:slug` (канон) и legacy `/spaces/:uuid`. Для slug резолвим
  // id через загруженный список пространств.
  const activeSpaceId = useMemo(() => {
    const legacy = location.pathname.match(/^\/spaces\/([0-9a-f-]+)/i);
    if (legacy) return legacy[1]!;
    const slugM = location.pathname.match(/^\/s\/([^/]+)/);
    if (slugM) {
      const slug = decodeURIComponent(slugM[1]!);
      return mySpaces.find((s) => s.slug === slug)?.id ?? null;
    }
    return null;
  }, [location.pathname, mySpaces]);
  const activeViewId = useMemo(() => {
    const m = location.pathname.match(
      /^\/(?:spaces\/[0-9a-f-]+|s\/[^/]+)\/views\/([0-9a-f-]+)/i,
    );
    return m ? m[1]! : null;
  }, [location.pathname]);

  // «Мои» пространства — для дерева рельса и команд палитры.
  const reloadSpaces = useCallback(() => {
    if (!user) {
      setMySpaces([]);
      return;
    }
    void listSpaces()
      .then((res) => {
        // Обычный пользователь: пространства с открытым доступом приходят в
        // `available` — показываем их в рельсе наравне со «своими» (доступ
        // как у участника). Platform-админу `available` — это ВСЕ прочие
        // пространства, ими рельс не заваливаем (у него они в разделе
        // «Доступные» на главной).
        setMySpaces(
          user.isPlatformAdmin ? res.mine : [...res.mine, ...res.available],
        );
      })
      .catch(() => {
        /* вспомогательное — глотаем */
      });
  }, [user]);

  useEffect(() => {
    reloadSpaces();
  }, [reloadSpaces]);

  // Непрочитанные по пространствам считает общий NotificationsProvider: тот же
  // срез нужен доске (по записи), и держать два поллинга ради одного запроса
  // незачем.

  const loadViews = useCallback((spaceId: string) => {
    setLoadingViews((prev) => {
      if (prev.has(spaceId)) return prev;
      const next = new Set(prev);
      next.add(spaceId);
      return next;
    });
    void listViews(spaceId)
      .then((res) => {
        const sorted = [...res.views]
          .filter((v) => !v.isArchived)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        setViewsBySpace((prev) => ({ ...prev, [spaceId]: sorted }));
      })
      .catch(() => {
        setViewsBySpace((prev) => ({ ...prev, [spaceId]: [] }));
      })
      .finally(() => {
        setLoadingViews((prev) => {
          const next = new Set(prev);
          next.delete(spaceId);
          return next;
        });
      });
  }, []);

  // ID доступных пользователю пространств — чтобы не запрашивать
  // представления чужих/удалённых пространств (иначе после входа под другим
  // пользователем persisted-`expanded` из localStorage прежней сессии давал
  // серию 403 на /views).
  const mySpaceIds = useMemo(() => new Set(mySpaces.map((s) => s.id)), [mySpaces]);

  // При смене пользователя (в т.ч. logout) сбрасываем кэш представлений —
  // иначе в рельсе/состоянии остаются данные прошлой сессии.
  useEffect(() => {
    setViewsBySpace({});
    setLoadingViews(new Set());
  }, [user]);

  // Рельс реагирует на мутации структуры пространств (создание/архивация/
  // удаление пространства, создание/удаление представления) из любого места
  // приложения — перезагружаем список пространств и представления уже
  // раскрытых/активного пространств, чтобы рельс не оставался несвежим.
  useEffect(() => {
    return onSpacesChanged(() => {
      reloadSpaces();
      const toReload = new Set(expanded);
      if (activeSpaceId) toReload.add(activeSpaceId);
      toReload.forEach((sid) => {
        if (mySpaceIds.has(sid)) loadViews(sid);
      });
    });
  }, [reloadSpaces, loadViews, expanded, activeSpaceId, mySpaceIds]);

  // Активное пространство — авто-раскрыть и подгрузить его представления.
  useEffect(() => {
    if (!activeSpaceId) return;
    setExpanded((prev) => {
      if (prev.has(activeSpaceId)) return prev;
      const next = new Set(prev);
      next.add(activeSpaceId);
      writeLs(LS_EXPANDED, JSON.stringify([...next]));
      return next;
    });
  }, [activeSpaceId]);

  // Подгрузить представления для раскрытых пространств, которых ещё нет в кэше.
  // Только для доступных пользователю пространств — чужие/удалённые из
  // persisted-`expanded` пропускаем, чтобы не ловить 403.
  useEffect(() => {
    for (const id of expanded) {
      if (mySpaceIds.has(id) && !viewsBySpace[id] && !loadingViews.has(id)) loadViews(id);
    }
  }, [expanded, viewsBySpace, loadingViews, loadViews, mySpaceIds]);

  const toggleSpace = useCallback((spaceId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(spaceId)) next.delete(spaceId);
      else next.add(spaceId);
      writeLs(LS_EXPANDED, JSON.stringify([...next]));
      return next;
    });
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeLs(LS_COLLAPSED, next ? '1' : '0');
      return next;
    });
  }, []);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  // Глобальный ⌘K / Ctrl+K.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        const editable =
          tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable === true;
        if (editable) return;
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleLogout = (): void => {
    void logout();
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <SettingOutlined />,
      label: 'Профиль',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'mcp-guide',
      icon: <ApiOutlined />,
      label: 'Как подключить MCP',
      onClick: () => navigate('/profile#mcp'),
    },
    ...(user?.isPlatformAdmin
      ? [
          {
            key: 'admin-users',
            icon: <TeamOutlined />,
            label: 'Пользователи',
            onClick: () => navigate('/admin/users'),
          },
          {
            key: 'admin-activity',
            icon: <HistoryOutlined />,
            label: 'Журнал действий',
            onClick: () => navigate('/admin/activity'),
          },
          {
            key: 'admin-settings',
            icon: <SettingOutlined />,
            label: 'Настройки платформы',
            onClick: () => navigate('/admin/settings'),
          },
        ]
      : []),
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выйти',
      danger: true,
      onClick: handleLogout,
    },
  ];

  const isSpaces =
    location.pathname === '/' ||
    location.pathname.startsWith('/spaces') ||
    location.pathname.startsWith('/s/');
  const isDictionaries = location.pathname.startsWith('/dictionaries');
  const isMy = location.pathname.startsWith('/my');

  const railClass = `${styles.rail} ${collapsed ? styles.railCollapsed : ''}`;

  // ── Навигационная ссылка (с поддержкой свёрнутого режима) ──
  const navLink = (
    to: string,
    icon: React.ReactNode,
    label: string,
    active: boolean,
    badge?: number,
  ): JSX.Element => {
    const inner = (
      <Link
        to={to}
        className={`${styles.navLink} ${active ? styles.navLinkActive : ''}`}
      >
        <span className={styles.navIcon}>
          <Badge count={badge ?? 0} size="small" offset={[2, -2]} dot={collapsed && !!badge}>
            {icon}
          </Badge>
        </span>
        {!collapsed && <span className={styles.navLabel}>{label}</span>}
      </Link>
    );
    return collapsed ? (
      <Tooltip key={to} title={label} placement="right">
        {inner}
      </Tooltip>
    ) : (
      inner
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={collapsed ? 64 : 248} className={railClass} theme="dark">
        <div className={styles.brandRow}>
          <Link to="/" className={styles.brand} data-testid="app-topbar-brand">
            <span className={styles.brandDot}><AppstoreFilled /></span>
            {!collapsed && <span className={styles.brandText}>Lens</span>}
          </Link>
          <Tooltip title={collapsed ? 'Развернуть меню' : 'Свернуть меню'} placement="right">
            <button
              type="button"
              className={styles.collapseBtn}
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </button>
          </Tooltip>
        </div>

        {collapsed ? (
          <Tooltip title="Поиск везде  ⌘K" placement="right">
            <button
              type="button"
              className={styles.searchTriggerMini}
              onClick={openPalette}
              aria-label="Поиск везде"
            >
              <SearchOutlined />
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            className={styles.searchTrigger}
            onClick={openPalette}
            aria-label="Поиск везде"
          >
            <SearchOutlined />
            <span className={styles.searchText}>Поиск везде…</span>
            <kbd className={styles.kbd}>⌘K</kbd>
          </button>
        )}

        <nav className={styles.nav}>
          {navLink('/', <AppstoreOutlined />, 'Пространства', isSpaces)}
          {navLink('/my', <CarryOutOutlined />, 'Моё', isMy)}
          {navLink('/dictionaries', <BookOutlined />, 'Справочники', isDictionaries)}
        </nav>

        {/* Избранное — закреплённые пространства/представления, свой порядок */}
        {!collapsed && (
          <FavoritesSection
            favorites={favorites}
            activeSpaceId={activeSpaceId}
            activeViewId={activeViewId}
            spaceNameById={spaceNameById}
          />
        )}

        {/* Дерево пространств с вложенными представлениями */}
        <div className={styles.tree}>
          {!collapsed && mySpaces.length > 0 && (
            <div className={styles.sectionLabel}>Пространства</div>
          )}
          <div className={styles.treeList}>
            {mySpaces.map((space) => {
              const spaceActive = activeSpaceId === space.id;
              const isOpen = expanded.has(space.id);
              const unread = unreadBySpace[space.id] ?? 0;
              const views = viewsBySpace[space.id];
              const isLoading = loadingViews.has(space.id);

              if (collapsed) {
                return (
                  <Tooltip key={space.id} title={space.name} placement="right">
                    <Link
                      to={`/s/${space.slug}`}
                      className={`${styles.spaceMini} ${spaceActive ? styles.spaceMiniActive : ''}`}
                    >
                      <Badge count={unread} size="small" dot offset={[-2, 2]}>
                        <SpaceAvatar name={space.name} icon={space.icon} seed={space.id} size={28} />
                      </Badge>
                    </Link>
                  </Tooltip>
                );
              }

              return (
                <div key={space.id} className={styles.spaceNode}>
                  <div
                    className={`${styles.spaceRow} ${spaceActive ? styles.spaceRowActive : ''}`}
                  >
                    <button
                      type="button"
                      className={styles.chevronBtn}
                      onClick={() => toggleSpace(space.id)}
                      aria-label={isOpen ? 'Свернуть представления' : 'Развернуть представления'}
                      aria-expanded={isOpen}
                    >
                      {isOpen ? <DownOutlined /> : <RightOutlined />}
                    </button>
                    <Link
                      to={`/s/${space.slug}`}
                      className={styles.spaceLink}
                      title={space.name}
                      onClick={() => {
                        if (!expanded.has(space.id)) toggleSpace(space.id);
                      }}
                    >
                      <SpaceAvatar name={space.name} icon={space.icon} seed={space.id} size={22} />
                      <span className={styles.spaceName}>{space.name}</span>
                      {unread > 0 && (
                        <Badge count={unread} size="small" className={styles.spaceBadge} />
                      )}
                    </Link>
                    {(() => {
                      const fav = favKeys.has(favoriteKey('space', space.id));
                      return (
                        <Tooltip
                          title={fav ? 'Убрать из избранного' : 'В избранное'}
                          placement="right"
                        >
                          <button
                            type="button"
                            className={`${styles.rowStar} ${fav ? styles.rowStarOn : ''}`}
                            aria-label={fav ? 'Убрать из избранного' : 'В избранное'}
                            aria-pressed={fav}
                            onClick={() =>
                              toggleFavorite({
                                kind: 'space',
                                id: space.id,
                                slug: space.slug,
                                name: space.name,
                                icon: space.icon,
                              })
                            }
                          >
                            {fav ? <StarFilled /> : <StarOutlined />}
                          </button>
                        </Tooltip>
                      );
                    })()}
                  </div>

                  {isOpen && (
                    <div className={styles.viewList}>
                      {isLoading && !views ? (
                        <div className={styles.viewLoading}>
                          <LoadingOutlined /> загрузка…
                        </div>
                      ) : views && views.length > 0 ? (
                        views.map((v) => {
                          const viewActive =
                            activeViewId === v.id ||
                            (spaceActive && !activeViewId && v.isDefault);
                          const fav = favKeys.has(favoriteKey('view', v.id));
                          return (
                            <div key={v.id} className={styles.viewRow}>
                              <Link
                                to={`/s/${space.slug}/views/${v.id}`}
                                className={`${styles.viewLink} ${viewActive ? styles.viewLinkActive : ''}`}
                                title={v.name}
                              >
                                <span className={styles.viewIcon}>{viewIcon(v)}</span>
                                <span className={styles.viewName}>{v.name}</span>
                              </Link>
                              <Tooltip
                                title={fav ? 'Убрать из избранного' : 'В избранное'}
                                placement="right"
                              >
                                <button
                                  type="button"
                                  className={`${styles.rowStar} ${fav ? styles.rowStarOn : ''}`}
                                  aria-label={fav ? 'Убрать из избранного' : 'В избранное'}
                                  aria-pressed={fav}
                                  onClick={() =>
                                    toggleFavorite({
                                      kind: 'view',
                                      id: v.id,
                                      spaceId: space.id,
                                      spaceSlug: space.slug,
                                      name: v.name,
                                      viewType: v.viewType,
                                      icon: v.icon,
                                    })
                                  }
                                >
                                  {fav ? <StarFilled /> : <StarOutlined />}
                                </button>
                              </Tooltip>
                            </div>
                          );
                        })
                      ) : (
                        <div className={styles.viewEmpty}>Нет представлений</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.railSpacer} />

        {user ? (
          <div className={`${styles.foot} ${collapsed ? styles.footCollapsed : ''}`}>
            <Tooltip
              title={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
              placement={collapsed ? 'right' : 'top'}
            >
              <button
                type="button"
                onClick={toggleTheme}
                aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
                className={styles.footIconBtn}
              >
                {mode === 'dark' ? <BulbFilled /> : <BulbOutlined />}
              </button>
            </Tooltip>
            <NotificationBell onRead={reloadUnread} />
            <ReleasesButton
              className={styles.footIconBtn}
              placement={collapsed ? 'right' : 'top'}
            />
            <JobsIndicator
              className={styles.footIconBtn}
              placement={collapsed ? 'right' : 'top'}
            />
            {hermesSessions.length > 0 ? (
              <Tooltip
                title={`Сессии оценки: ${hermesSessions.length}`}
                placement={collapsed ? 'right' : 'top'}
              >
                <button
                  type="button"
                  className={styles.footIconBtn}
                  aria-label={`Сессии оценки: ${hermesSessions.length}`}
                  data-testid="hermes-sessions-indicator"
                  onClick={() => (hermesOpen ? closeHermesDrawer() : showHermesDrawer())}
                >
                  <Badge count={hermesSessions.length} size="small">
                    <MessageOutlined />
                  </Badge>
                </button>
              </Tooltip>
            ) : null}
            {canUseAi ? (
              <Tooltip title="Lensy" placement={collapsed ? 'right' : 'top'}>
                <div
                  className={styles.aiButton}
                  onClick={() => togglePanel()}
                  role="button"
                  tabIndex={0}
                  aria-label="Lensy"
                >
                  <AiSparkIcon className={styles.aiButtonIcon} />
                </div>
              </Tooltip>
            ) : null}
            <Dropdown
              menu={{ items: userMenuItems }}
              trigger={['click']}
              placement="topLeft"
            >
              <div
                className={styles.userDropdown}
                data-testid="app-topbar-user"
                title={user.displayName}
              >
                <div
                  className={styles.avatar}
                  style={
                    user.avatarUrl
                      ? {
                          backgroundImage: `url(${JSON.stringify(user.avatarUrl)})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          color: 'transparent',
                        }
                      : undefined
                  }
                >
                  {!user.avatarUrl && getInitials(user.displayName)}
                </div>
              </div>
            </Dropdown>
          </div>
        ) : null}
      </Sider>

      <Layout
        style={{ marginRight: hermesShift, transition: 'margin-right 0.2s ease' }}
      >
        <Content className={styles.content}>{children}</Content>
      </Layout>

      <CommandPalette open={paletteOpen} onClose={closePalette} spaces={mySpaces} />
      <AiChatPanel />
      <HermesSessionDock />
    </Layout>
  );
}
